import { useLanguage } from '../contexts/LanguageContext';
import type { SupportedLanguage } from '../utils/translations';

export interface FAQItem {
  question: string;
  answer: string;
}

export interface SupportTranslations {
  title: string;
  subtitle: string;
  contactHeader: string;
  contactEmail: string;
  contactEmailDesc: string;
  responseTime: string;
  responseDesc: string;
  faqHeader: string;
  faqSubtitle: string;
  faqs: FAQItem[];
  channelsHeader: string;
  delistHeader: string;
  delistDesc: string;
  delistLink: string;
  crawlerHeader: string;
  crawlerDesc: string;
  crawlerLink: string;
}

export const supportTranslations: Record<SupportedLanguage, SupportTranslations> = {
  en: {
    title: 'Customer Support & Help Center',
    subtitle: 'How to reach the PriceLoss team and get help with price tracking and AI integrations.',
    contactHeader: 'Contact Us',
    contactEmail: 'support@priceloss.com',
    contactEmailDesc: 'For account assistance, tracking questions, or AI plugin inquiries.',
    responseTime: '24-48 business hours',
    responseDesc: 'We respond to all verified customer inquiries within 24-48 business hours.',
    faqHeader: 'Frequently Asked Questions',
    faqSubtitle: 'Guidance for price tracking, notifications, and AI integrations.',
    faqs: [
      {
        question: 'How do I connect ChatGPT / MCP plugins?',
        answer:
          'From your AI client, start the PriceLoss connection flow to authorize your account. You will review requested permissions (priceloss:read, priceloss:write, priceloss:events) before granting access. Connections can be revoked at any time in your dashboard.',
      },
      {
        question: 'How do I manage tracked products and alert thresholds?',
        answer:
          'Submit a product URL on the web dashboard or browser extension to preview price details. Choose an alert threshold (any drop, 5%, 10%, 15%) and confirm. You can adjust thresholds or pause monitoring from your watchlist.',
      },
      {
        question: 'How do notifications work (real-time events vs fallback)?',
        answer:
          'When connected through an AI integration that supports native events, verified price drops trigger real-time notifications. Email and browser extension alerts remain active as reliable fallbacks.',
      },
    ],
    channelsHeader: 'Other Channels & Inquiries',
    delistHeader: 'Merchant & Store Inquiries',
    delistDesc: 'Are you a merchant or store administrator requesting domain exclusion?',
    delistLink: 'Merchant Delisting & Abuse Contact',
    crawlerHeader: 'Crawler & Bot Transparency',
    crawlerDesc: 'Need technical details regarding our polite crawler, rate limits, or robots.txt?',
    crawlerLink: 'PriceLossBot Information',
  },
  ar: {
    title: 'مركز المساعدة والدعم',
    subtitle: 'كيفية التواصل مع فريق برايس لوس والحصول على المساعدة بشأن تتبع الأسعار وتكاملات الذكاء الاصطناعي.',
    contactHeader: 'تواصل معنا',
    contactEmail: 'support@priceloss.com',
    contactEmailDesc: 'للمساعدة في الحساب أو أسئلة تتبع الأسعار أو استفسارات إضافات الذكاء الاصطناعي.',
    responseTime: '24-48 ساعة عمل',
    responseDesc: 'نرد على جميع استفسارات العملاء المؤكدة خلال 24-48 ساعة عمل.',
    faqHeader: 'الأسئلة الشائعة',
    faqSubtitle: 'إرشادات حول تتبع الأسعار والإشعارات وتكاملات الذكاء الاصطناعي.',
    faqs: [
      {
        question: 'كيف يمكنني ربط إضافات ChatGPT / MCP؟',
        answer:
          'من خلال مساعد الذكاء الاصطناعي، ابدأ تدفق الاتصال بـ PriceLoss لتفويض حسابك. ستراجع الأذونات المطلوبة (القراءة، التعديل، الأحداث) قبل الموافقة. يمكنك إلغاء الاتصال في أي وقت من لوحة التحكم.',
      },
      {
        question: 'كيف أدير المنتجات المتتبعة ونسب التنبيه؟',
        answer:
          'أدخل رابط المنتج في لوحة التحكم أو إضافة المتصفح لمعاينة الأسعار. حدد نسبة التنبيه (أي انخفاض، 5%، 10%، 15%) وأكد المراقبة. يمكنك تعديل النسب أو إيقاف التتبع من قائمة المراقبة.',
      },
      {
        question: 'كيف تعمل الإشعارات (الأحداث الفورية مقابل البدائل)؟',
        answer:
          'عند الاتصال عبر تكامل ذكاء اصطناعي يدعم الأحداث الأصلية، يتم إرسال تنبيهات فورية عند انخفاض السعر المؤكد. تظل تنبيهات البريد الإلكتروني وإضافة المتصفح نشطة كبديل موثوق.',
      },
    ],
    channelsHeader: 'قنوات واستفسارات أخرى',
    delistHeader: 'استفسارات المتاجر والتجار',
    delistDesc: 'هل أنت ممثل لمتجر إلكتروني ترغب في استبعاد نطاقك من شبكة الفحص؟',
    delistLink: 'استبعاد المتاجر والتواصل بشأن الاستخدام',
    crawlerHeader: 'شفافية برنامج الفحص',
    crawlerDesc: 'هل تبحث عن التفاصيل التقنية حول PriceLossBot وحدود الطلبات وملف robots.txt؟',
    crawlerLink: 'معلومات PriceLossBot',
  },
  fr: {
    title: "Centre d'aide et support client",
    subtitle: "Comment contacter l'équipe PriceLoss et obtenir de l'aide pour le suivi des prix.",
    contactHeader: 'Contactez-nous',
    contactEmail: 'support@priceloss.com',
    contactEmailDesc: 'Pour toute assistance relative à votre compte ou aux intégrations IA.',
    responseTime: '24-48 heures ouvrables',
    responseDesc: 'Nous répondons à toutes les demandes vérifiées dans un délai de 24 à 48 heures.',
    faqHeader: 'Foire Aux Questions',
    faqSubtitle: 'Conseils pour le suivi des prix, les alertes et les plugins IA.',
    faqs: [
      {
        question: 'Comment connecter les plugins ChatGPT / MCP ?',
        answer:
          'Depuis votre client IA, lancez le flux de connexion PriceLoss pour autoriser votre compte avec les portées requises.',
      },
      {
        question: 'Comment gérer les produits suivis et les seuils d’alerte ?',
        answer:
          'Soumettez une URL de produit sur le tableau de bord pour prévisualiser le prix et définir votre seuil de baisse.',
      },
      {
        question: 'Comment fonctionnent les notifications ?',
        answer:
          'Les baisses confirmées déclenchent des notifications en temps réel sur les hôtes IA pris en charge, avec relais par e-mail.',
      },
    ],
    channelsHeader: 'Autres canaux et demandes',
    delistHeader: 'Demandes des marchands',
    delistDesc: 'Êtes-vous un administrateur souhaitant exclure votre domaine de notre surveillance ?',
    delistLink: 'Déréférencement marchand & abus',
    crawlerHeader: 'Transparence du robot',
    crawlerDesc: 'Détails techniques sur notre robot PriceLossBot et le respect du robots.txt.',
    crawlerLink: 'Informations PriceLossBot',
  },
  pt: {
    title: 'Central de Ajuda e Suporte ao Cliente',
    subtitle: 'Como entrar em contato com a equipe PriceLoss e obter suporte para rastreamento de preços.',
    contactHeader: 'Fale Conosco',
    contactEmail: 'support@priceloss.com',
    contactEmailDesc: 'Para assistência de conta, rastreamento ou dúvidas sobre plugins de IA.',
    responseTime: '24-48 horas úteis',
    responseDesc: 'Respondemos a todas as dúvidas verificadas dentro de 24 a 48 horas úteis.',
    faqHeader: 'Perguntas Frequentes',
    faqSubtitle: 'Orientações sobre monitoramento de preços, alertas e integrações de IA.',
    faqs: [
      {
        question: 'Como conectar plugins do ChatGPT / MCP?',
        answer:
          'No seu cliente de IA, inicie o fluxo de conexão PriceLoss para autorizar sua conta com os escopos solicitados.',
      },
      {
        question: 'Como gerenciar produtos rastreados e limites de alerta?',
        answer:
          'Envie uma URL de produto no painel para visualizar o preço e escolher seu limite de alerta de queda.',
      },
      {
        question: 'Como funcionam as notificações?',
        answer:
          'Quedas confirmadas disparam notificações em tempo real em plataformas compatíveis, com suporte por e-mail.',
      },
    ],
    channelsHeader: 'Outros canais e consultas',
    delistHeader: 'Comerciantes e Lojistas',
    delistDesc: 'Você é administrador de uma loja e deseja excluir seu domínio do monitoramento?',
    delistLink: 'Deslistagem de Comerciantes & Abuso',
    crawlerHeader: 'Transparência do Rastreador',
    crawlerDesc: 'Detalhes técnicos sobre nosso rastreador PriceLossBot e conformidade com robots.txt.',
    crawlerLink: 'Informações do PriceLossBot',
  },
  es: {
    title: 'Centro de Ayuda y Soporte al Cliente',
    subtitle: 'Cómo comunicarse con el equipo de PriceLoss y obtener ayuda con el seguimiento de precios.',
    contactHeader: 'Contáctenos',
    contactEmail: 'support@priceloss.com',
    contactEmailDesc: 'Para soporte con su cuenta, seguimiento de precios o consultas sobre plugins de IA.',
    responseTime: '24-48 horas hábiles',
    responseDesc: 'Respondemos a todas las consultas verificadas en un plazo de 24 a 48 horas hábiles.',
    faqHeader: 'Preguntas Frecuentes',
    faqSubtitle: 'Guía para seguimiento de precios, alertas e integraciones de IA.',
    faqs: [
      {
        question: '¿Cómo conectar plugins de ChatGPT / MCP?',
        answer:
          'Desde su cliente de IA, inicie el flujo de conexión de PriceLoss para autorizar su cuenta con los permisos necesarios.',
      },
      {
        question: '¿Cómo gestionar productos rastreados y umbrales de alerta?',
        answer:
          'Envíe la URL de un producto en el panel para previsualizar el precio y seleccionar el umbral de alerta.',
      },
      {
        question: '¿Cómo funcionan las notificaciones?',
        answer:
          'Las bajadas confirmadas activan notificaciones en tiempo real en plataformas compatibles, con respaldo por correo.',
      },
    ],
    channelsHeader: 'Otros canales y consultas',
    delistHeader: 'Comerciantes y Tiendas',
    delistDesc: '¿Es usted administrador de una tienda y desea excluir su dominio del rastreo?',
    delistLink: 'Exclusión de Comerciantes y Contacto de Abuso',
    crawlerHeader: 'Transparencia del Rastreador',
    crawlerDesc: 'Detalles técnicos sobre PriceLossBot, límites de frecuencia y robots.txt.',
    crawlerLink: 'Información de PriceLossBot',
  },
};

export function useSupportTranslation(): SupportTranslations {
  const { language } = useLanguage();
  return supportTranslations[language] ?? supportTranslations.en;
}
